<template>
  <validation-provider
    #default="{ errors }"
    :name="item.label"
    :vid="item.name || item.key"
    :rules="item.rules"
    :custom-messages="item.customMessages"
    :mode="item.type === 'image' ? 'aggressive' : 'eager'"
  >
    <b-form-group
      :label-for="id || item.name || item.key"
      class="mb-0 mt-[24px] [&_input]:bg-white [&_input]:placeholder:text-[#C2C2C2]"
    >
      <template #label>
        <span
          class="text-[#333] text-[14px] mb-[8px] block"
        >
          {{ item.label }}<span
            v-if="isRequiredRule || item.required"
            class="text-[#F95031]"
          >*</span>
        </span>
      </template>
      <b-input-group
        v-if="item.type === 'string' || item.type === 'number'"
        :class="{ 'has-error': errors.length > 0 }"
      >
        <template
          v-if="item.prefix"
          #append
        >
          <b-input-group-text class="!text-[#828282]">
            {{ item.prefix }}
          </b-input-group-text>
        </template>
        <b-form-input
          :id="id || item.name || item.key"
          v-model="localValue"
          :type="item.type === 'string' ? 'text' : item.type"
          :name="item.name || item.key"
          :placeholder="item.placeholder"
          :state="errors.length > 0 ? false : null"
          :prefix="item.prefix"
          :min="item.type === 'number' ? 0 : undefined"
          :disabled="item.disabled"
          class="!bg-none !pr-[0.75rem] !px-[14px] !py-[10px] !rounded-[8px]"
          :style="item.disabled && disabledBgColor ? { backgroundColor: disabledBgColor + ' !important' } : {}"
          :class="{
            '!rounded-r-[0px]': item.prefix
          }"
          @keypress="onKeyPress"
        />
      </b-input-group>
      <b-form-textarea
        v-if="item.type === 'text'"
        :id="id || item.name || item.key"
        v-model="localValue"
        :name="item.name || item.key"
        :placeholder="item.placeholder"
        :state="errors.length > 0 ? false : null"
        :disabled="item.disabled"
        :rows="3"
        class="!bg-none !pr-[0.75rem] !px-[14px] !py-[10px] !rounded-[8px]"
        :style="item.disabled && disabledBgColor ? { backgroundColor: disabledBgColor + ' !important' } : {}"
      />
      <v-select
        v-if="item.type === 'select'"
        :id="id || item.name || item.key"
        v-model="localValue"
        :placeholder="item.placeholder"
        :state="errors.length > 0 ? false : null"
        :disabled="item.disabled"
        :options="formattedSelectOptions"
        :reduce="option => option.value"
        class="custom-v-select bg-white"
        :class="{ 'is-invalid': errors.length > 0 }"
        :style="item.disabled && disabledBgColor ? { '--vs-state-disabled-bg': disabledBgColor } : {}"
      />
      <multi-select
        v-if="item.type === 'multiselect'"
        :id="id || item.name || item.key"
        v-model="localValue"
        no-label
        searchable
        :disabled="item.disabled"
        :disabled-bg-color="disabledBgColor"
        :trigger-class="[
          'px-[14px] py-[8px] bg-white',
          errors.length > 0 ? '!border-[#ea5455]' : 'border-[#E2E2E2]'
        ]"
        :placeholder="item.placeholder"
        :options="formattedMultiSelectOptions"
        :has-next-page="optionFields.hasMore || false"
        :is-loading="optionFields.isLoading || false"
        :no-select-all="item.noSelectAll || false"
        @on-intersect="optionFields.fetchData && optionFields.fetchData()"
        @on-search="onMultiSelectSearch"
      >
        <template #placeholder="options">
          <div class="grow m-0">
            <category-pill
              v-if="options.selected.length > 0"
              container-class="text-[14px] font-normal text-[#6e6b7b]"
              :category="options.selected.map(v => multiSelectLabelsMap[v] || (item.key === 'domicile' && typeof v === 'string' ? v.replace(', ', ' · ') : v))"
              :max-displayed-item="3"
            />
            <span
              v-else
              class="text-[14px] font-normal text-[#C2C2C2]"
            >
              {{ item.placeholder }}
            </span>
          </div>
        </template>
        <template #error>
          <p class="text-[12px] text-[#828282] m-[8px] flex items-center justify-center gap-[8px]">
            Tidak ada data yang ditampilkan.
          </p>
        </template>
      </multi-select>
      <image-upload
        v-if="item.type === 'image'"
        v-model="localValue"
        :name="item.name || item.key"
        :placeholder="item.placeholder"
        :disabled="item.disabled"
        :disabled-bg-color="disabledBgColor"
        :class="{ 'is-invalid': errors.length > 0 }"
      />
      <datepicker
        v-if="item.type === 'date'"
        :id="id || item.name || item.key"
        v-model="localValue"
        :name="item.name || item.key"
        :placeholder="item.placeholder"
        :state="errors.length > 0 ? false : null"
        :disabled-dates="item.disabledDates"
        :disabled="item.disabled"
        class="datepicker-with-icon p-0 [&_input]:border-[1px] [&_input]:border-[#E2E2E2] [&_input]:rounded-[8px] [&_input]:px-[14px] [&_input]:py-[10px] [&_input]:w-full"
        :class="[
          item.disabled ? 'opacity-60 pointer-events-none is-disabled' : '',
          !item.disabled ? '[&_input]:bg-white' : '',
          errors.length > 0 ? '[&_input]:!border-[#ea5455]' : ''
        ]"
        :style="item.disabled && disabledBgColor ? { '--datepicker-disabled-bg': disabledBgColor } : {}"
      />

      <div class="flex items-center justify-between gap-[8px] mt-[8px]">
        <div>
          <small
            v-if="errors[0]"
            class="text-danger block text-[12px] mb-[2px]"
          >
            {{ errors[0] }}
          </small>
          <small
            v-if="item.footer"
            class="text-[#626262] text-[12px] block"
          >{{ item.footer }}</small>
        </div>
        <small
          v-if="maxRule"
          class="text-[12px] block shrink-0"
          :class="currentLength > maxRule ? '!text-[#E31A1A] font-semibold' : 'text-[#626262]'"
        >
          {{ currentLength }}/{{ maxRule }}
        </small>
      </div>
    </b-form-group>
  </validation-provider>
</template>

<script>
import { ValidationProvider } from 'vee-validate'
import VSelect from 'vue-select'
import Datepicker from 'vuejs-datepicker'
import { MultiSelect } from '@/views/components/select'
import CategoryPill from '../../creator-management/CategoryPill.vue'
import ImageUpload from './ImageUpload.vue'
import { useCampaignOptions } from '../composables/useCampaignOptions'
import '@validations'

export default {
  name: 'ValidatedInput',
  components: {
    ValidationProvider,
    VSelect,
    Datepicker,
    MultiSelect,
    CategoryPill,
    ImageUpload,
  },
  props: {
    value: {
      type: [String, Number, Array, Object, Date],
      default: '',
    },
    item: {
      type: Object,
      default: () => ({}),
    },
    id: {
      type: String,
      default: '',
    },
    disabledBgColor: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      localValue: this.value,
      /**
       * State reaktif untuk opsi select/multiselect.
       * Diinisialisasi sekali di created() agar tidak menyebabkan
       * infinite re-initialization pada computed property.
       */
      optionFieldsState: null,
    }
  },
  computed: {
    extractedRules() {
      if (typeof this.item.rules !== 'string') return []
      return this.item.rules.split('|').map(rule => {
        const [name, value] = rule.split(':')
        return { name, value }
      })
    },
    isRequiredRule() {
      return this.extractedRules.some(rule => rule.name === 'required') || this.item.required
    },
    maxRule() {
      const maxRule = this.extractedRules.find(rule => rule.name === 'max')
      return maxRule?.value ? Number(maxRule.value) : null
    },
    currentLength() {
      return this.localValue ? String(this.localValue).length : 0
    },
    optionFields() {
      if (this.optionFieldsState) return this.optionFieldsState
      return {
        loading: false,
        error: undefined,
        data: this.item.options,
      }
    },
    optionsList() {
      const raw = this.optionFields?.data
      // createPaginatedFetcher menyimpan data sebagai flat array langsung di .data
      if (Array.isArray(raw)) return raw
      // Non-paginated (useCampaignOptions biasa) menyimpan sebagai { data: [...] }
      if (raw && Array.isArray(raw.data)) return raw.data
      return this.item.options || []
    },
    formattedMultiSelectOptions() {
      const result = { value: [], text: [], uncheckOthers: [] }

      if (Array.isArray(this.item.prependOptions)) {
        this.item.prependOptions.forEach(opt => {
          result.value.push(opt.value)
          result.text.push(opt.label ?? opt.text ?? opt.value)
          result.uncheckOthers.push(Boolean(opt.uncheckOthers))
        })
      }

      this.optionsList.forEach(rawItem => {
        const val = typeof this.item.optionValue === 'function'
          ? this.item.optionValue(rawItem)
          : (rawItem?.option_label ?? rawItem?.value ?? rawItem)

        const label = typeof this.item.optionLabel === 'function'
          ? this.item.optionLabel(rawItem)
          : (rawItem?.option_label ?? rawItem?.label ?? rawItem?.text ?? val)

        result.value.push(val)
        result.text.push(label)
        result.uncheckOthers.push(Boolean(rawItem?.uncheckOthers))
      })

      return result
    },
    formattedSelectOptions() {
      return this.optionsList.map(option => ({
        value: typeof this.item.optionValue === 'function' ? this.item.optionValue(option) : (option?.option_label ?? option?.value),
        label: typeof this.item.optionLabel === 'function' ? this.item.optionLabel(option) : (option?.option_label ?? option?.label),
      }))
    },
    multiSelectLabelsMap() {
      const { value, text } = this.formattedMultiSelectOptions
      return Object.fromEntries(value.map((v, i) => [v, text[i]]))
    },
  },
  watch: {
    value(newVal) {
      this.localValue = newVal
    },
    localValue(newVal) {
      this.$emit('input', newVal)
      if (this.item.type === 'number' && newVal !== '' && newVal !== null && newVal !== undefined) {
        const cleaned = String(newVal).replace(/\D/g, '')
        if (cleaned !== String(newVal)) {
          this.localValue = cleaned
        }
      }
    },
  },
  created() {
    if (this.item.type === 'select' || this.item.type === 'multiselect') {
      this.optionFieldsState = useCampaignOptions(
        this.$http_new,
        { option_type: this.item.key },
        this.$http,
      )
    }
  },
  methods: {
    onKeyPress(event) {
      if (this.item.type === 'number') {
        const char = String.fromCharCode(event.which || event.keyCode)
        if (!/[0-9]/.test(char)) {
          event.preventDefault()
        }
      }
    },
    /**
     * Handler untuk event `on-search` dari komponen MultiSelect.
     * Meneruskan query ke method `search` pada optionFieldsState
     * sehingga data difetch ulang dari server berdasarkan kata kunci.
     *
     * @param {string} query
     */
    onMultiSelectSearch(query) {
      if (this.item.key === 'domicile' && this.optionFieldsState && typeof this.optionFieldsState.search === 'function') {
        this.optionFieldsState.search(query)
      }
    },
  },
}
</script>

<style lang="scss" scoped>
::v-deep .form-control.is-invalid {
  border-color: #ea5455 !important;
  background-image: none !important;
  padding-right: 0.75rem !important;
}
.has-error ::v-deep .input-group-text {
  border-color: #ea5455 !important;
}
.is-invalid ::v-deep .vs__dropdown-toggle {
  border-color: #ea5455 !important;
}
.datepicker-with-icon {
  position: relative;
  &::after {
    content: '\e9a5';
    font-family: 'komerce';
    position: absolute;
    right: 14px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 24px;
    color: #292D32;
    pointer-events: none;
    font-weight: normal;
  }
}
.datepicker-with-icon.is-disabled ::v-deep input {
  background-color: var(--datepicker-disabled-bg) !important;
}
.custom-v-select ::v-deep .vs__dropdown-toggle {
  padding: 8px 14px !important;
  border-radius: 8px;
  border-color: #E2E2E2;
  .vs__selected-options {
    padding: 0;
  }
  .vs__search, .vs__selected {
    padding: 0;
    margin: 0;
  }
}
</style>
